// Работният ден на заведението — денят, който може да свършва СЛЕД полунощ.
//
// Сметка, закрита в 00:23, е от вчерашната смяна, не от новия ден. Дотук
// справките режеха на календарна полунощ и точно тези сметки скачаха в „днес",
// без нищо да го обяснява.
//
// Границите идват от РАБОТНОТО ВРЕМЕ (Конфигурация → Работно време,
// `lib/workingHours.ts`) — за всеки ден от седмицата поотделно: денят е от
// отварянето до затварянето му (10:00 → 23:00, или 10:00 → 02:00 на
// следващата дата). Почивен ден е целият календарен ден (от края на
// предишния, ако той е работил след полунощ). Докато работното време НЕ е
// настроено, остава старото правило: граница в ЧАС ОТ ДЕНОНОЩИЕТО (по
// подразбиране 06:00) → 06:00 → 05:59 на следващата дата.
//
// „Работна дата" на един момент е датата на работния ден, към който
// принадлежи: след полунощ, до затварянето на вчерашния ден — вчера; иначе
// календарната дата.
//
//   петък 10:00–02:00:  събота 01:30  →  работна дата петък
//                       събота 08:00  →  работна дата събота
//
// Работното време е САМО филтър по подразбиране — периодът в пикера може да
// се смени ръчно; нищо друго (BE групирането по дни, дашбордът) не го чете.
// Правилата са обяснени с примери на екрана „Работно време" (преводите
// `workingHours.guide` — раздела `reports`) — смениш ли ги тук, смени и текста там.
//
// БЕКЕНДЪТ НЕ СЕ ПРОМЕНЯ. `_restrict_to_closed_bills` сравнява цели моменти, а
// `_per_day_time_filters` има изричен клон за „обърнат прозорец" (start.time()
// > end.time()), който също минава на цели моменти — там попада всеки ден
// след полунощ („10:00 → 02:00"). Необърнат прозорец за няколко дни
// („10:00 пн → 23:00 нд") BE чете като „всеки ден 10:00–23:00" — с часа на
// отварянето на ПЪРВИЯ ден и на затварянето на ПОСЛЕДНИЯ.
//
// КАКВО ТОВА НЕ ПОКРИВА: групирането ПО ДНИ на BE още реже в полунощ
// (`_local_day` в `service_ordered_items_by_day.py`, „Оборот по дни" на
// дашборда, хистограмата на активността). Сборовете са верни, но един работен
// ден се вижда като две колонки.
//
// БЕЗ РАБОТЕН ДЕН Е „Фактури" — търси се по датата върху документа
// (`issue_date`, без час), затова там нарочно се ползват `startOf("day")` /
// `endOf("day")`. „Картови плащания" е по работното време само когато то е
// настроено; иначе — календарният ден, както преди настройката.

import type { Dayjs } from "dayjs";
import type { WorkingDay } from "../api/configuration";
import {
  crossesMidnight,
  getWorkingHours,
  minutesOfTime,
  workingDayOf,
} from "./workingHours";

/** Часът по подразбиране, когато нищо не е зададено (без работно време). */
const DEFAULT_START_HOUR = 6;

/**
 * Границата в часове след полунощ — само докато работното време не е
 * настроено.
 *
 * Идва от `public/config.js` (`window.__ENV`), който бие `import.meta.env` —
 * така всяко заведение си слага своя час без пребилдване, точно както адресът
 * на API-то. `0` изключва цялото поведение: работният ден става календарен.
 */
export const businessDayStartHour = (): number => {
  const raw =
    window.__ENV?.VITE_BUSINESS_DAY_START ??
    import.meta.env.VITE_BUSINESS_DAY_START;
  if (raw === undefined || raw === null || raw === "") return DEFAULT_START_HOUR;
  const hour = Number(raw);
  // Счупена стойност НЕ бива да мести деня на случаен час — по-добре
  // подразбирането, отколкото тихо разминаване в числата.
  if (!Number.isFinite(hour) || hour < 0 || hour > 23) return DEFAULT_START_HOUR;
  return Math.floor(hour);
};

/** Работното време на датата (по деня от седмицата); null — не е настроено. */
const workingDayOfDate = (date: Dayjs): WorkingDay | null => workingDayOf(date.day());

/**
 * Датата с час "HH:MM". Часът се СЛАГА (`.hour()`/`.minute()`), а не се
 * ДОБАВЯ (`.add(h, "hour")`): добавянето е абсолютно и в нощта на смяната на
 * часа изместваше границата — на 29.03 началото ставаше 07:00, а на 25.10 —
 * 05:00. Тогава прозорецът излизаше НЕОБЪРНАТ, бекендът го четеше като час
 * от денонощието и справката падаше на нула. Слагането е по стенен часовник
 * и е стабилно и в двете нощи.
 */
const atTime = (date: Dayjs, time: string): Dayjs => {
  const minutes = minutesOfTime(time);
  return date.startOf("day").hour(Math.floor(minutes / 60)).minute(minutes % 60);
};

/**
 * Работната ДАТА на един момент — началото на календарния ден, към който
 * принадлежи смяната. Това е сърцето на поправката: в 00:23 „днес" е вчера.
 */
export const businessDateOf = (moment: Dayjs): Dayjs => {
  const date = moment.startOf("day");
  if (!getWorkingHours()) {
    return moment.hour() < businessDayStartHour() ? date.subtract(1, "day") : date;
  }
  const day = workingDayOfDate(date);
  if (day && !day.closed && !moment.isBefore(atTime(date, day.opensAt))) return date;
  // След полунощ, до затварянето на вчерашния ден (включително) — вчера.
  // BE не позволява вчерашният ден да свършва след днешното отваряне.
  const previousDate = date.subtract(1, "day");
  const previous = workingDayOfDate(previousDate);
  if (previous && crossesMidnight(previous) && !moment.isAfter(atTime(date, previous.closesAt))) {
    return previousDate;
  }
  return date;
};

/** Началото на работния ден, чиято работна дата е `date`. */
export const startOfBusinessDay = (date: Dayjs): Dayjs => {
  if (!getWorkingHours()) return date.startOf("day").hour(businessDayStartHour());
  const day = workingDayOfDate(date);
  if (day && !day.closed) return atTime(date, day.opensAt);
  // Почивен ден: от полунощ — или секунда след края на вчерашния ден, ако той
  // работи след полунощ (иначе двата дни делят момент и `businessDateOf` би
  // върнал началото на почивния ден към вчера).
  const previous = workingDayOfDate(date.subtract(1, "day"));
  return previous && crossesMidnight(previous)
    ? atTime(date, previous.closesAt).add(1, "second")
    : date.startOf("day");
};

/**
 * Краят на работния ден, чиято работна дата е `date` — затварянето (на
 * следващата дата, ако денят работи след полунощ). Почивен ден свършва в края
 * на календарния ден. Без работно време — последната секунда преди
 * следващата граница.
 */
export const endOfBusinessDay = (date: Dayjs): Dayjs => {
  if (!getWorkingHours()) {
    return date
      .startOf("day")
      .add(1, "day")
      .hour(businessDayStartHour())
      .subtract(1, "second");
  }
  const day = workingDayOfDate(date);
  if (!day || day.closed) return date.endOf("day");
  return atTime(crossesMidnight(day) ? date.add(1, "day") : date, day.closesAt);
};

/** Работната дата на СЕГАШНИЯ момент — „днес" в смисъла на смяната. */
export const currentBusinessDate = (now: Dayjs): Dayjs => businessDateOf(now);

/**
 * Началото на работния ден, В КОЙТО ПОПАДА `moment` — пряката замяна на
 * `moment.startOf("day")`.
 *
 * Идемпотентна е: подаден вече клампнат момент се връща непроменен. Точно това
 * позволява подмяната да е механична — справките клампват периода на няколко
 * места по пътя и двойното прилагане не бива да мести нищо.
 */
export const businessDayStartFrom = (moment: Dayjs): Dayjs =>
  startOfBusinessDay(businessDateOf(moment));

/**
 * Краят на работния ден, В КОЙТО ПОПАДА `moment` — пряката замяна на
 * `moment.endOf("day")`. Също идемпотентна.
 */
export const businessDayEndFrom = (moment: Dayjs): Dayjs =>
  endOfBusinessDay(businessDateOf(moment));


/* ─────────────────────────── календарен или работен ─────────────────────── */

/**
 * Кой ден гледа екранът.
 *
 * `ReportDatePicker` и `useReportsDateParam` се ползват и ИЗВЪН справките —
 * цялата Складова база, „Одит на действията" и ФМ отчетът в Маркирането стъпват
 * на тях. Работният ден е верният отговор само за търговските справки; за
 * останалите „днес" си е календарният ден. Затова режимът е ИЗРИЧЕН и по
 * подразбиране е календарен: екран, който не знае за работния ден, не бива да
 * получи прозорец през полунощ и да пита за два дни.
 */
export type DayMode = "calendar" | "business";

/** Трите операции „кой ден е това" и „докъде се простира", според режима. */
export const dayBounds = (mode: DayMode) =>
  mode === "business"
    ? {
        dateOf: businessDateOf,
        startOf: startOfBusinessDay,
        endOf: endOfBusinessDay,
      }
    : {
        dateOf: (moment: Dayjs) => moment.startOf("day"),
        startOf: (date: Dayjs) => date.startOf("day"),
        endOf: (date: Dayjs) => date.endOf("day"),
      };
